import isEqual from "lodash/isEqual";
import * as React from "react";

import { useVizDispatch } from "@/shared/state/visualization";
import { setPlotPanelConfig } from "@/shared/state/visualization/actions";
import {
  PlotPanelConfig,
  ViewportSyncMode,
} from "@/shared/state/visualization/schema";
import {
  PlotAxisConfig,
  PlotPanelState,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";

import { effectiveXAxisLabel } from "../effectiveXAxisLabel";
import { type Axis } from "../messaging";
import { usePlotPanelContext } from "../panelContext";
import { DEFAULT_VIEWPORT_SYNC_MODE } from "../panelContext/effectiveViewportSyncMode";
import { PlotManager } from "../PlotManager";
import type { Extents as RendererExtents } from "../PlotRenderer";

type AxisToConfig = Partial<Record<Axis, PlotAxisConfig>>;
/**
 * Applies changes in the plot config state to the current viewport of the panel.
 */
export function useOnAxisConfigChanged(
  plotManager: PlotManager | null,
  state: PlotPanelState,
) {
  const incomingAxisToConfig: AxisToConfig = React.useMemo(() => {
    const result: AxisToConfig = {
      // x is present even when nothing is saved under xAxis: the loop below
      // skips an absent entry, so clearing the alignment would otherwise leave
      // the last derived label on the plot. The derived label reaches only the
      // plot manager, never viz state, so it cannot overwrite the user's own.
      x: {
        ...state.config?.xAxis,
        label: effectiveXAxisLabel(
          state.config?.xAxis?.label,
          state.config?.xAxisAlignment,
        ),
      },
      y: state.config?.yAxis,
    };
    for (const [k, axisConfig] of Object.entries(
      state.config?.extraYAxes ?? {},
    )) {
      const key = k as Axis;
      result[key] = axisConfig;
    }
    return result;
  }, [
    state.config?.xAxis,
    state.config?.xAxisAlignment,
    state.config?.yAxis,
    state.config?.extraYAxes,
  ]);

  const axisToConfig = React.useRef(incomingAxisToConfig);
  const plotManagerRef = React.useRef(plotManager);

  React.useEffect(
    function updateAxisConfig() {
      if (plotManager === null) {
        return;
      }
      for (const key of Object.keys(incomingAxisToConfig)) {
        const axis = key as Axis;
        const incoming = incomingAxisToConfig[axis];
        if (!incoming) {
          continue;
        }
        const current = axisToConfig.current[axis];
        // Update the axis config only when that axis specifically changed,
        // or when a new plot manager is being used
        // (like when the plot manager initializes)
        if (
          !isEqual(incoming, current) ||
          plotManager !== plotManagerRef.current
        ) {
          plotManager.updateAxisConfig(axis, incoming, {
            overrideChartBounds:
              plotManager !== plotManagerRef.current &&
              // The override of the chart bounds only works when the y axis is locked.
              // Otherwise, the plot data streamer's data extents is the source of truth
              // for the y axis range and it will overwrite the assigned bounds
              // during active data loads.
              incomingAxisToConfig.y?.bounds?.locked,
          });
        }
      }
      axisToConfig.current = incomingAxisToConfig;
      plotManagerRef.current = plotManager;
    },
    [plotManager, incomingAxisToConfig],
  );
}

const SAVE_AXIS_DEBOUNCE_MS = 33;

/**
 * Applies the current viewport of the panel to the plot config state.
 *
 * This allows us to show changes to the viewport in the panel settings view
 * in near-real-time.
 */
export function useSaveAxisBounds(
  chartExtents: RendererExtents | null,
  panelId: string,
  config: PlotPanelConfig | undefined,
) {
  const dispatch = useVizDispatch();
  const { viewportDomain, viewportRange, viewportExtraRanges } =
    chartExtents?.data ?? {};
  const debouncedDispatch = React.useRef<number>();
  const configUpdatesRef = React.useRef<Partial<PlotPanelConfig>>();

  React.useEffect(() => {
    function shouldUpdateConfigBounds(
      axisConfig: PlotAxisConfig | undefined,
      chartBounds: { min: number; max: number },
    ) {
      return (
        axisConfig?.bounds?.max !== chartBounds?.max ||
        axisConfig?.bounds?.min !== chartBounds?.min
      );
    }
    const configUpdates: Partial<PlotPanelConfig> = {};

    // Add x axis updates
    if (
      viewportDomain &&
      shouldUpdateConfigBounds(config?.xAxis, viewportDomain)
    ) {
      configUpdates.xAxis = {
        bounds: { ...viewportDomain, setBy: "chart" },
      };
    }

    // Add y axis updates
    if (
      viewportRange &&
      !config?.yAxis?.bounds?.locked &&
      shouldUpdateConfigBounds(config?.yAxis, viewportRange)
    ) {
      // TODO(ENG-1429): support multiple y axes
      configUpdates.yAxis = {
        bounds: {
          ...viewportRange,
          locked: config?.yAxis?.bounds?.locked,
          setBy: "chart",
        },
      };
    }

    if (viewportExtraRanges && !config?.yAxis?.bounds?.locked) {
      Object.entries(viewportExtraRanges).forEach(([key, range]) => {
        const axis = key as PlotYAxis;
        if (
          range &&
          shouldUpdateConfigBounds(config?.extraYAxes?.[axis], range)
        ) {
          configUpdates.extraYAxes ??= {};
          configUpdates.extraYAxes[axis] = {
            bounds: {
              ...range,
              setBy: "chart",
            },
          };
        }
      });
    }

    // Schedule the updates
    configUpdatesRef.current = configUpdates;
    if (Object.keys(configUpdates).length > 0) {
      if (!debouncedDispatch.current) {
        debouncedDispatch.current = window.setTimeout(
          function dispatchWithDebounce() {
            debouncedDispatch.current = undefined;
            if (
              configUpdatesRef.current &&
              Object.keys(configUpdatesRef.current).length > 0
            ) {
              dispatch(setPlotPanelConfig(panelId, configUpdatesRef.current));
            }
          },
          SAVE_AXIS_DEBOUNCE_MS,
        );
      }
    }
  }, [
    dispatch,
    panelId,
    config,
    viewportDomain,
    viewportRange,
    viewportExtraRanges,
  ]);

  React.useEffect(function clearDebouncedDispatchOnUnmount() {
    return function onUnmount() {
      if (debouncedDispatch.current !== undefined) {
        window.clearTimeout(debouncedDispatch.current);
      }
    };
  }, []);
}

/**
 * Prevents panels from broadcasting axes that have been locked.
 *
 * Without this, panels with divergent y-axis ranges would try to sync with each other,
 * which can cause fairly unpleasant unpredictable behavior.
 */
export function useDisableSyncOnLockedAxes(
  config: PlotPanelConfig | undefined,
) {
  const { setViewportSyncMode: setSyncMode } = usePlotPanelContext();

  // The persisted setting, not the panel context's effective mode. While the x
  // axis is aligned the effective mode is "" whatever the user persisted, so
  // reading it would find no "y" to strip, and the rising-edge check below would
  // still consume the lock. Un-aligning would then restore a persisted "xy"
  // against a locked axis, the state this hook exists to prevent.
  const syncMode = config?.viewportSyncMode ?? DEFAULT_VIEWPORT_SYNC_MODE;

  // TODO(ENG-1429): Support multiple axes.
  const isYLocked = !!config?.yAxis?.bounds?.locked;
  const previousYLocked = React.useRef(false);
  React.useEffect(
    function disableSyncOnLockedAxes() {
      if (isYLocked && !previousYLocked.current && syncMode.includes("y")) {
        // If the lock was just turned on, disable the locked axis
        // and preserve all other settings.
        setSyncMode(noYSyncModes[syncMode]);
      }
      previousYLocked.current = isYLocked;
    },
    [syncMode, setSyncMode, isYLocked],
  );
}

const noYSyncModes: Record<ViewportSyncMode, ViewportSyncMode> = {
  "": "",
  y: "",
  x: "x",
  xy: "x",
};
